/**
 * Single source of truth for the six booking add-ons (the "reconciled add-on
 * matrix", plan D-2). Every enumeration of add-ons elsewhere — QuoteOptions
 * keys, inquiry zod fields, bookings columns, driver-message lines, pricing
 * meta sources — derives from (or is compile-checked against) this registry.
 *
 * Leaf module: imports NOTHING (shared <- engine <- server import direction;
 * BANDS stays leaf in engine/bands.ts — see plan M1 "watch import direction").
 *
 * D-2 invariant: `validFor: 'all'` is the all-for-all "valid/settable" column —
 * every service may SET every add-on (the kiosk sends them; enforcement clamps
 * values and strips only unknown keys). Pricing APPLICABILITY is the engine's
 * job (`pricesFor` + the service guards in variance.ts), never the allowlist's.
 */

export type AddOnValueKind = 'bool' | 'count';

/** pricing meta key an add-on's figure flows from (pricing.json meta ->
 *  flatten -> rate-snapshot meta -> app_meta seed). M3 wires the two new ones. */
export type AddOnPriceSource = 'meet_and_greet' | 'extra_pickup' | 'trailer';

/** Which services the ENGINE prices the add-on for:
 *  'airport'  = airport grid only (guard in variance.ts)
 *  'baseLine' = any service with a computed base line (grid + point-to-point)
 *  'none'     = flag-only, never priced */
export type AddOnPricesFor = 'airport' | 'baseLine' | 'none';

export type AddOnDescriptor = {
  /** Canonical engine key — the QuoteOptions property name. */
  engineKey: string;
  /** Public /api/inquiry field name; absent = not captured on the public form. */
  inquiryField?: string;
  /** `bookings` column (migration v8: meet_greet/return_trip/accessible_vehicle;
   *  v9 adds extra_pickups/late_night/trailer). */
  dbColumn: string;
  /** Kiosk-facing label. */
  label: string;
  valueKind: AddOnValueKind;
  /** Inclusive bounds for 'count' values (extraPickups cap mirrors the live
   *  kiosk stepper, server/public/app.js `>= 5` guards — raise here, one line). */
  bounds?: { readonly min: number; readonly max: number };
  /** Whether the engine prices it (returnTrip prices via the return rule, no
   *  meta source; trailer €50 ratified 2026-07-13, wired in M3). */
  priced: boolean;
  priceSource?: AddOnPriceSource;
  pricesFor: AddOnPricesFor;
  /** D-2: always 'all' — see module doc. */
  validFor: 'all';
  /** Driver-message line label (M4 renders one line per SET add-on).
   *  ASCII only — no em/en dashes or curly quotes (driver-message file rule). */
  driverLine: string;
  /**
   * ⛔ REQUIRED — the Article 9 outbound decision for this add-on, per add-on,
   * decided once here rather than at each call site. `'send'` = the value may
   * leave the estate to the WhatsApp/SMS message processor in the operator
   * alert; `'withhold'` = it may not, and the alert must carry no rendering of
   * it. There is deliberately NO default: a seventh add-on cannot leak by
   * inaction, because omitting this field is a COMPILE ERROR (ADR1-D30 rule 3,
   * "the allowlist is the type" — reused here for the outbound seam).
   *
   * The test each verdict answers is stated verbatim in
   * `test/shared/addons-schema.spec.ts`; apply the SAME one to a new add-on.
   *
   * ⚠️ CONDITIONAL, do not let a future line inherit a clearance it never
   * earned: ADR1-D30 (`docs/decisions/ADR-0001-portal-kiosk-intake-seam.md`
   * §8.3) rules a RECURRENCE PATTERN a clinical inference ("every Tuesday and
   * Friday" against a named individual reads as dialysis). The driver message
   * carries no recurrence line today, so that inference is unreachable and the
   * five `'send'` verdicts below are clear of it. It becomes reachable the day
   * a recurrence/schedule line is added to the message — at which point every
   * verdict here must be re-assessed, not assumed to still hold.
   */
  outboundToProcessor: 'send' | 'withhold';
  /**
   * ⛔ REQUIRED — can this add-on make a HAND-PRICED total exceed the each-way
   * BAND BASE? Decided once here rather than at each call site, and deliberately
   * with NO default: a seventh add-on cannot silently contaminate the price book
   * by inaction, because omitting this field is a COMPILE ERROR (the same
   * "the allowlist is the type" mechanic as `outboundToProcessor` above).
   *
   * WHY IT EXISTS (T897, a live money defect): the venue-evidence arm proposes a
   * past job's whole-journey `final_total_cents` as a venue's STANDING EACH-WAY
   * base. Standing prices are each-way (`docs/pricing-spec.md:99`, Q30, Owen
   * 2026-07-17) and a hand-priced total is INCLUSIVE (`:94`), so a wedding
   * hand-priced at EUR800 for a return with a trailer proposed a EUR800 each-way
   * base - roughly 2.3x the real one.
   *
   * ⛔ THIS IS NOT `priced`, AND MUST NOT BE "SIMPLIFIED" INTO IT. `priced` says
   * whether the ENGINE adds a line. This says whether HELEN's own figure may
   * already carry the cost. They disagree on `lateNight`: the engine prices it at
   * EUR0, but `docs/pricing-spec.md:96` rules it "NOT a fixed surcharge... Helen
   * decides", so a hand-priced late-night job may carry a premium of unknown
   * size. Deriving this set from `priced` reinstates the T897 defect.
   *
   * There is no third state for "inflates by a known amount": nothing here is
   * recoverable by arithmetic. `quote_versions` records two money columns and
   * BOTH are whole-journey - no per-leg base was ever stored - so a contaminated
   * total cannot be decomposed, only withheld.
   */
  inflatesAgreedTotal: 'yes' | 'no';
  /**
   * ⛔ REQUIRED — what the CUSTOMER is told this add-on is, or `null` for "never
   * shown to a customer". Decided once here rather than at each call site, and
   * deliberately with NO default, exactly like the two fields above: a seventh
   * add-on cannot silently reach a customer by inaction, because omitting this
   * is a COMPILE ERROR.
   *
   * WHY IT EXISTS (T1679, board #1679): a real customer asked for a return on
   * the website form, was quoted a price that DOES include it, could not tell
   * from the quote email, and emailed Helen instead of paying. The quote message
   * stated the date, the route and the band, and nothing about what the price
   * covered.
   *
   * ⛔ THIS IS NOT `outboundToProcessor`, AND MUST NOT BE DERIVED FROM IT.
   * That field's docblock scopes it to the WhatsApp/SMS operator alert; this is
   * the CUSTOMER plane, which differs on recipient (the data subject herself),
   * processor (the email transport) and purpose. They agree today on
   * `accessibleVehicle` and they agree for DIFFERENT REASONS - a right answer
   * for a wrong reason is how add-on #7 gets a wrong one. The invariant that
   * every `outboundToProcessor: 'withhold'` add-on is also `customerLine: null`
   * is asserted in `test/shared/addons-schema.spec.ts` rather than assumed, so
   * the coupling is enforced instead of coincidental.
   *
   * ⛔ IT IS ALSO NOT `inflatesAgreedTotal`. That says whether the add-on may
   * have moved the money; this says whether the customer is told the job
   * includes it. `lateNight` is the case that separates them: it inflates a
   * hand-priced total, but the journey TIME is already the first line of every
   * quote, and the engine adds EUR0 - so naming it would imply a charge that
   * was not made while telling the customer nothing they cannot already read.
   *
   * ⚠️ A 'count' add-on's value is the SINGULAR STEM ("extra pickup"), because
   * the renderer composes "3 extra pickups" / "1 extra pickup" around it. That
   * works only because the one count add-on has a regular plural; a future count
   * add-on with an irregular one needs a shape change here, not a special case
   * at the sink.
   *
   * ⚠️ NO COMMAS, and plain ASCII. The SMS joins these parts with ", ", so a
   * comma inside one reads as two items; `scripts/check-copy.sh` gates this file
   * for em/en dashes and curly quotes.
   */
  customerLine: string | null;
};

/** The ONE canonical late-night window (docs/pricing-spec.md: flag-only,
 *  no fixed surcharge). Every message/label derives from this constant. */
export const LATE_NIGHT_WINDOW = { start: '22:00', end: '06:00' } as const;

/** ASCII label for the window, e.g. "22:00-06:00" (plain hyphen, never an en-dash). */
export const LATE_NIGHT_WINDOW_LABEL = `${LATE_NIGHT_WINDOW.start}-${LATE_NIGHT_WINDOW.end}`;

export const ADDONS = [
  {
    engineKey: 'meetAndGreet',
    inquiryField: 'meetGreet',
    dbColumn: 'meet_greet',
    label: 'Meet & greet',
    valueKind: 'bool',
    priced: true,
    priceSource: 'meet_and_greet',
    pricesFor: 'airport',
    validFor: 'all',
    driverLine: 'Meet & greet',
    // VERDICT send: a terminal greeting service the operator buys; supports no inference about the passenger's health, disability or any Article 9 category.
    outboundToProcessor: 'send',
    // VERDICT yes: EUR25 (docs/pricing-spec.md:93). Charged once even on a return.
    inflatesAgreedTotal: 'yes',
    // VERDICT shown: a service the customer explicitly asked for and is paying for; naming it is how she can tell the terminal greeting is in the price.
    customerLine: 'Meet and greet at the terminal',
  },
  {
    engineKey: 'extraPickups',
    dbColumn: 'extra_pickups',
    label: 'Extra pickups',
    valueKind: 'count',
    bounds: { min: 0, max: 5 },
    priced: true,
    priceSource: 'extra_pickup',
    pricesFor: 'baseLine',
    validFor: 'all',
    driverLine: 'Extra pickups',
    // VERDICT send: a count of additional collection stops - a routing fact about the journey, not about any person on it.
    outboundToProcessor: 'send',
    // VERDICT yes: EUR10 per extra pickup (docs/pricing-spec.md:95), and one of the three components a return doubles.
    inflatesAgreedTotal: 'yes',
    // VERDICT shown: extra collection stops are a routing fact the customer stated and a line she is paying for. SINGULAR STEM - the renderer composes "3 extra pickups" around it.
    customerLine: 'extra pickup',
  },
  {
    engineKey: 'returnTrip',
    inquiryField: 'returnTrip',
    dbColumn: 'return_trip',
    label: 'Return trip',
    valueKind: 'bool',
    priced: true,
    pricesFor: 'baseLine',
    validFor: 'all',
    driverLine: 'Return trip',
    // VERDICT send: the shape of ONE journey (out and back). Not a recurrence pattern, so ADR1-D30's clinical-rhythm inference does not attach.
    outboundToProcessor: 'send',
    // VERDICT yes: the loudest case. A return DOUBLES base + extra pickups + trailer (Q30, Owen 2026-07-17, docs/pricing-spec.md:99), so a return job's total is roughly twice its own each-way base.
    inflatesAgreedTotal: 'yes',
    // VERDICT shown: THE add-on board #1679 was raised for. A customer asked for a return on the website form, was quoted a price that included it, and could not tell.
    customerLine: 'Return journey out and back',
  },
  {
    engineKey: 'lateNight',
    dbColumn: 'late_night',
    label: 'Late night',
    valueKind: 'bool',
    priced: false,
    pricesFor: 'none',
    validFor: 'all',
    driverLine: `Late night (${LATE_NIGHT_WINDOW.start}-${LATE_NIGHT_WINDOW.end})`,
    // VERDICT send: the time-of-day window of a single trip - a scheduling fact; a lone late hour carries no health inference (see the recurrence conditional above).
    outboundToProcessor: 'send',
    // ⛔ THE ONE THAT DISAGREES WITH `priced` ABOVE - do not "align" them. The ENGINE
    // adds EUR0 here (priced: false). But a hand-priced total is HELEN's figure, and
    // unknown magnitude is exactly the case that must be withheld, never computed.
    // VERDICT yes: docs/pricing-spec.md:96 rules late night "NOT a fixed surcharge. Show a flag + a suggested range; Helen decides", so her total may already carry a night premium that nothing records.
    inflatesAgreedTotal: 'yes',
    // ⛔ THE ONE THAT DISAGREES WITH `inflatesAgreedTotal` ABOVE - do not "align" them, for the
    // mirror of the reason that field disagrees with `priced`. Helen's total MAY carry a night
    // premium, so the money verdict is 'yes'. But the journey TIME is already the first line of
    // every quote message, so this line would tell the customer nothing she cannot read; and the
    // ENGINE adds EUR0, so naming it as something the quote covers implies a charge not made.
    // VERDICT hidden: the journey time already states it, and the engine prices it at zero.
    customerLine: null,
  },
  {
    engineKey: 'trailer',
    dbColumn: 'trailer',
    label: 'Trailer',
    valueKind: 'bool',
    priced: true,
    priceSource: 'trailer',
    pricesFor: 'baseLine',
    validFor: 'all',
    driverLine: 'Trailer',
    // VERDICT send: luggage/equipment capacity attached to the VEHICLE (golf clubs, cases); says nothing about a passenger's condition.
    outboundToProcessor: 'send',
    // VERDICT yes: EUR50 flat per job, and it doubles on a return (docs/pricing-spec.md:94).
    inflatesAgreedTotal: 'yes',
    // VERDICT shown: a EUR50 line the customer asked for, and the one add-on whose ABSENCE she would discover at the kerb with luggage that does not fit.
    customerLine: 'Trailer for luggage or equipment',
  },
  {
    engineKey: 'accessibleVehicle',
    inquiryField: 'accessibleVehicle',
    dbColumn: 'accessible_vehicle',
    label: 'Accessible vehicle',
    valueKind: 'bool',
    priced: false,
    pricesFor: 'none',
    validFor: 'all',
    driverLine: 'Accessible vehicle',
    // VERDICT withhold: a wheelchair/bariatric requirement, joined to the name + phone in the same message, IS a disability fact about an identified person - GDPR Article 9 (same class the append-only booking_events already excludes, booking-events-repo.ts:11-15; ADR1-D30 Q59).
    outboundToProcessor: 'withhold',
    // ⛔ The ONLY 'no', and it is a ruling, not an oversight. Flipping it to 'yes' would
    // withhold evidence from precisely the wheelchair / bariatric work that is a FLAGSHIP
    // service here, degrading its price book while every other service's improves. It
    // looks like prudent hardening and is not; do not "harden" it.
    // VERDICT no: an accessible vehicle is "Same price, allocate a suitable vehicle" (engine/variance.ts accessibleVehicleVariance; the flag message was re-punctuated at T1723 M4, II-flow-F4, and says the same thing) - absent from the pricing-spec add-on table because it carries no charge.
    inflatesAgreedTotal: 'no',
    // ⛔ REASONED ON ITS OWN, NOT COPIED FROM `outboundToProcessor` ABOVE - see the field docblock.
    // That verdict is about the WhatsApp/SMS processor; this one is about the customer's own email.
    // They land the same way here and would even if the recipient were the data subject herself: a
    // wheelchair/bariatric requirement is Article 9 special-category data, and the quote email
    // leaves the estate through a transport processor exactly as the operator alert does.
    // ⚠️ The `withhold` -> `null` invariant is ASSERTED in test/shared/addons-schema.spec.ts. It is
    // a cross-check on two independently-reached verdicts, never the derivation of one from the other.
    // VERDICT hidden: GDPR Article 9 - a disability fact about an identified person (ADR1-D30 Q59).
    customerLine: null,
  },
] as const satisfies readonly AddOnDescriptor[];

type AddOns = typeof ADDONS;

/** Canonical engine keys: 'meetAndGreet' | 'extraPickups' | ... */
export type AddOnKey = AddOns[number]['engineKey'];

/** Public-form field names the registry maps onto engine keys. */
export type InquiryAddOnField = Extract<AddOns[number], { inquiryField: string }>['inquiryField'];

/** Homomorphic tuple map — a GENERIC alias so TS preserves tuple-ness (an
 *  inline mapped type over the concrete tuple would enumerate array members). */
type EngineKeysOf<T> = { [K in keyof T]: T[K] extends { engineKey: infer V } ? V : never };

/** Tuple of engine keys (literal-typed, zod-enum ready). */
export const ADDON_KEYS = ADDONS.map((a) => a.engineKey) as unknown as EngineKeysOf<AddOns>;

/** engineKey -> full descriptor. */
export const ADDON_BY_KEY = Object.fromEntries(ADDONS.map((a) => [a.engineKey, a])) as {
  [K in AddOnKey]: Extract<AddOns[number], { engineKey: K }>;
};

/** engineKey -> bookings column. */
export const ADDON_DB_COLUMNS = Object.fromEntries(
  ADDONS.map((a) => [a.engineKey, a.dbColumn]),
) as { [K in AddOnKey]: Extract<AddOns[number], { engineKey: K }>['dbColumn'] };

/** engineKey -> kiosk label. */
export const ADDON_LABELS = Object.fromEntries(ADDONS.map((a) => [a.engineKey, a.label])) as {
  [K in AddOnKey]: string;
};

/** Every `bookings` add-on column name, literal-typed. */
export type AddOnDbColumn = AddOns[number]['dbColumn'];

/**
 * The `bookings` columns whose presence means an agreed total may exceed the
 * each-way band base. DERIVED from ADDONS, never hand-listed: a seventh add-on
 * joins this set the moment its `inflatesAgreedTotal` verdict is recorded, which
 * is the whole point of making that field required.
 */
export const INFLATING_ADDON_COLUMNS: readonly AddOnDbColumn[] = ADDONS
  .filter((a) => a.inflatesAgreedTotal === 'yes')
  .map((a) => a.dbColumn);

/**
 * A `bookings` row's add-on columns. Structural, so every caller passes what it
 * already has (`BookingRow`, or the evidence query's own row type) with no
 * mapping layer.
 *
 * ⚠️ IT HAS TWO CONSUMERS NOW, AND THEY READ IT IN OPPOSITE DIRECTIONS. This
 * docblock said "as far as this PREDICATE is concerned" when
 * `agreedTotalIsEachWayBase` below was the only one; T1679 added the customer
 * quote message (`notify/customer-message.ts`), which renders one line per SET
 * add-on. The two disagree deliberately on what an unusable value means:
 *   - the predicate FAILS SAFE TOWARD WITHHOLDING - null/absent/odd counts as
 *     SET, because on a money path the safe failure is refusing to propose;
 *   - the renderer FAILS SAFE TOWARD SILENCE - only `=== true` or a positive
 *     number renders, because there the risk is telling a customer her quote
 *     covers a service nobody booked.
 * Neither is the general rule. A third consumer must state its own direction
 * rather than copy whichever one it read first.
 */
export type AgreedTotalFlags = { readonly [K in AddOnDbColumn]?: unknown };

/**
 * Is this booking's AGREED TOTAL a like-for-like each-way band base (T897)?
 *
 * False means the recorded total covers more journey than one leg at the band
 * rate - a return, or a priced add-on - so it must never be proposed as a
 * standing base. There is no "adjust it" branch: `quote_versions` stores only
 * whole-journey money, so a contaminated total cannot be decomposed (see
 * `inflatesAgreedTotal` above).
 *
 * ⛔ FAIL-SAFE DIRECTION, load-bearing: only an explicit `false` (boolean) or a
 * non-positive count reads as clean. A missing, null, or unexpectedly-typed
 * column counts as SET and the price is WITHHELD. On a money path the safe
 * failure is refusing to propose, never proposing.
 * ⚠️ Do NOT rewrite the body as `if (row[col])` / `!row[col]`. That looks
 * equivalent and handles the count correctly, but it INVERTS this for `null` and
 * `undefined` (falsy reads as clean), which is the single most likely way to
 * silently reintroduce T897. It is pinned by the null-column spec.
 */
export function agreedTotalIsEachWayBase(row: AgreedTotalFlags): boolean {
  return INFLATING_ADDON_COLUMNS.every((col) => {
    const value = row[col];
    if (value === false) return true;                       // boolean flag, off
    if (typeof value === 'number') return value <= 0;       // count add-on, none
    return false;                                           // true / null / absent / odd -> withhold
  });
}

/**
 * Client-safe projection of an add-on descriptor (G1, M2 client rebuild):
 * everything the kiosk UI needs to render a toggle/stepper — and nothing
 * server-internal (dbColumn, inquiryField, priceSource, driverLine and
 * outboundToProcessor are NOT projected). `outboundToProcessor` is an egress
 * decision: deliberately absent from this type and from the projection below,
 * so `/api/service-descriptors` does not publish it. Pinned in
 * `test/shared/addons-schema.spec.ts`.
 *
 * ⚠️ SCOPE OF THAT GUARANTEE — do NOT generalise it to "the browser never sees
 * these". It is about the API projection, never about secrecy from the client.
 * `src/shared` is a leaf module bundled STRAIGHT INTO `src/server/public/client.js`
 * (via `src/client/store/registry.ts`), and the committed pre-M1 bundle already
 * carries dbColumn, driverLine, inquiryField and priceSource — measured, not
 * assumed; `outboundToProcessor` will ship there too on the next `build:client`.
 * That is pre-existing and tolerated ONLY because every one of these is a policy
 * constant holding no personal data. Anything genuinely secret must not live in
 * this module at all.
 */
export type ClientAddOnDescriptor = {
  engineKey: AddOnKey;
  label: string;
  valueKind: AddOnValueKind;
  bounds?: { readonly min: number; readonly max: number };
  priced: boolean;
  pricesFor: AddOnPricesFor;
  validFor: 'all';
};

/** Serialize the registry for `/api/service-descriptors` (no duplicated data —
 *  a projection of ADDONS, derived at call time). */
export function clientAddOnDescriptors(): ClientAddOnDescriptor[] {
  return ADDONS.map((a) => ({
    engineKey: a.engineKey,
    label: a.label,
    valueKind: a.valueKind,
    ...('bounds' in a ? { bounds: a.bounds } : {}),
    priced: a.priced,
    pricesFor: a.pricesFor,
    validFor: a.validFor,
  }));
}

/** Clamp a raw value to a valid 'count' add-on value: an integer within
 *  bounds; anything unparseable (NaN/Infinity/objects) -> bounds.min. VALUES
 *  are clamped, keys never dropped (D-2 — operator input is operational truth). */
export function clampCount(value: unknown, bounds: { min: number; max: number }): number {
  const n = Math.trunc(Number(value));
  if (!Number.isFinite(n)) return bounds.min;
  return Math.min(bounds.max, Math.max(bounds.min, n));
}
